import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useMemo, useState } from 'react';
import { fetchBrandManagers, } from '@/entities/brand-manager';
import { ApiError } from '@/shared/api';
import { agentCountLabel, uiCopy } from '@/shared/copy';
import { Alert, AlertDescription, AlertTitle, Button, Skeleton } from '@/shared/ui';
const FIELD_CLASS = 'min-h-[var(--unity-hit-target)] w-full rounded-[var(--unity-radius)] border border-border bg-[var(--unity-color-elevated)] px-3 text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50';
export function BrandManagerPicker({ busy = false, excludeManagerId, title = uiCopy.pickBrandManager, onPick, }) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [query, setQuery] = useState('');
    useEffect(() => {
        let cancelled = false;
        void (async () => {
            setLoading(true);
            setError(null);
            try {
                const data = await fetchBrandManagers();
                if (!cancelled)
                    setItems(data.items);
            }
            catch (err) {
                if (!cancelled) {
                    setError(err instanceof ApiError ? err.message : uiCopy.loadFailed);
                }
            }
            finally {
                if (!cancelled)
                    setLoading(false);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, []);
    const filtered = useMemo(() => {
        const needle = query.trim().toLowerCase();
        const source = excludeManagerId
            ? items.filter((item) => item.managerId !== excludeManagerId)
            : items;
        const sorted = [...source].sort((left, right) => {
            if (left.isCurrent !== right.isCurrent)
                return left.isCurrent ? -1 : 1;
            return left.displayName.localeCompare(right.displayName, 'ru');
        });
        if (!needle)
            return sorted;
        return sorted.filter((item) => item.displayName.toLowerCase().includes(needle));
    }, [excludeManagerId, items, query]);
    if (loading)
        return _jsx(Skeleton, { className: "h-40 w-full" });
    if (error) {
        return (_jsxs(Alert, { variant: "destructive", children: [_jsx(AlertTitle, { children: uiCopy.errorTitle }), _jsx(AlertDescription, { children: error })] }));
    }
    if (items.length === 0) {
        return (_jsxs(Alert, { children: [_jsx(AlertTitle, { children: uiCopy.brandManagersEmptyTitle }), _jsx(AlertDescription, { children: uiCopy.brandManagersEmptyHint })] }));
    }
    return (_jsxs("div", { className: "space-y-3", children: [_jsx("p", { className: "text-sm font-medium", children: title }), _jsx("input", { type: "search", value: query, onChange: (event) => setQuery(event.target.value), placeholder: uiCopy.brandManagerSearch, className: FIELD_CLASS, "aria-label": uiCopy.brandManagerSearch }), filtered.length === 0 ? (_jsx("p", { className: "text-sm text-muted-foreground", children: "\u041D\u0438\u043A\u043E\u0433\u043E \u043D\u0435 \u043D\u0430\u0448\u043B\u0438 \u043F\u043E \u044D\u0442\u043E\u043C\u0443 \u0437\u0430\u043F\u0440\u043E\u0441\u0443." })) : (_jsx("ul", { className: "divide-y divide-border rounded-md border border-border", children: filtered.map((manager) => (_jsx("li", { children: _jsx(Button, { type: "button", variant: "ghost", disabled: busy, className: "h-auto w-full justify-between rounded-none px-3 py-3 text-left", onClick: () => onPick(manager), children: _jsxs("span", { className: "min-w-0", children: [_jsxs("span", { className: "block truncate font-medium", children: [manager.displayName, manager.isCurrent ? (_jsx("span", { className: "ml-2 text-xs font-normal text-muted-foreground", children: uiCopy.currentManagerBadge })) : null] }), _jsx("span", { className: "block text-xs font-normal text-muted-foreground", children: agentCountLabel(manager.agentCount) })] }) }) }, manager.managerId))) }))] }));
}
